import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import conf from '../../conf/conf';
import { useSettings } from 'context/SettingsContext';
import { formatFileSize } from 'utils/helpers/gimHelper';
import { COLLAPSED_STORAGE_KEY, readCollapsedState } from './helper/tariffHelper';
import TariffUsageBar from './TariffUsageBar';
import './Tariff.css';

const BYTES_IN_GB = 1024 * 1024 * 1024;

const SideBarTariff = () => {
    const { t } = useTranslation('Tariff');
    const { settings } = useSettings();
    const account = settings?.account;
    const [collapsed, setCollapsed] = useState(readCollapsedState);

    if (!conf.saasMode || !account) return null;

    const tariffKey = String(account.tariff ?? '');
    const isFreeTariff = Number(account.tariff) === 0;
    const isCollapsed = !isFreeTariff && collapsed;
    const tariffName = t(`tariffName.${tariffKey}`, { defaultValue: tariffKey || '—' });
    const expireAt = Number(account.dtExpire || 0);
    const isExpired = !isFreeTariff && expireAt > 0 && expireAt < Date.now();
    const expireLabel = expireAt > 0
        ? DateTime.fromMillis(expireAt).toLocaleString(DateTime.DATE_MED)
        : '—';

    const objectsUsed = Number(settings?.objectsCreatedYear || 0);
    const objectsLimit = Number(account.maxObject || 0);
    const objectsRemaining = Math.max(objectsLimit - objectsUsed, 0);

    const storageUsed = Number(settings?.cloudFilesSize || 0);
    const storageLimitBytes = Number(account.maxSpace || 0) * BYTES_IN_GB;
    const storageRemaining = Math.max(storageLimitBytes - storageUsed, 0);

    const toggleCollapsed = () => {
        if (isFreeTariff) return;
        setCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
            return next;
        });
    };

    return (
        <div className={`sidebar-tariff ${isCollapsed ? 'sidebar-tariff--collapsed' : ''} ${isFreeTariff ? 'sidebar-tariff--free' : ''}`}>
            <div className="sidebar-tariff__card">
                <div className="sidebar-tariff__header">
                    <div className="sidebar-tariff__header-top">
                        <span className="sidebar-tariff__badge">{tariffName}</span>
                        {!isFreeTariff && (
                            <button
                                type="button"
                                className="sidebar-tariff__toggle"
                                onClick={toggleCollapsed}
                                aria-label={isCollapsed ? t('expand') : t('collapse')}
                                aria-expanded={!isCollapsed}
                            >
                                <span className="sidebar-tariff__toggle-icon" />
                            </button>
                        )}
                    </div>

                    {!isCollapsed && (
                        <div className={`sidebar-tariff__expire ${isExpired || isFreeTariff ? 'sidebar-tariff__expire--expired' : ''}`}>
                            {isFreeTariff
                                ? t('freeReadOnly')
                                : isExpired
                                    ? t('expired')
                                    : `${t('expiresAt')}: ${expireLabel}`}
                        </div>
                    )}
                </div>

                {!isCollapsed && (
                    <div className="sidebar-tariff__metrics">
                        <TariffUsageBar
                            label={t('objects')}
                            used={objectsUsed}
                            limit={objectsLimit}
                            remainingLabel={t('remainingCount', { value: objectsRemaining })}
                        />

                        <TariffUsageBar
                            label={t('storage')}
                            used={storageUsed}
                            limit={storageLimitBytes}
                            formatValue={(value) => formatFileSize(value) || '0 B'}
                            remainingLabel={t('remainingSize', { value: formatFileSize(storageRemaining) || '0 B' })}
                        />
                    </div>
                )}

                {!isCollapsed && (
                    <Link to="/tariff" className="sidebar-tariff__change hide-sidebar">
                        {t('changeTariff')}
                    </Link>
                )}
            </div>
        </div>
    );
};

export default SideBarTariff;
